import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiErrorDetail } from "@/lib/api";
import { ArcButton } from "@/components/ArcButton";
import { LabelBadge } from "@/components/LabelBadge";
import { ArrowLeft, Plus, Trash2, Pencil, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";

export default function QuizManager() {
  const [quizzes, setQuizzes] = useState([]);
  const load = () => api.get("/quizzes").then((r) => setQuizzes(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const del = async (slug) => {
    if (!window.confirm("Delete this quiz and its leaderboard? This cannot be undone.")) return;
    try { await api.delete(`/quizzes/${slug}`); toast.success("Quiz deleted"); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  return (
    <div className="mx-auto max-w-4xl px-4 py-10 sm:px-6">
      <Link to="/admin" className="inline-flex items-center gap-1 text-sm hover:underline" style={{ color: "var(--arc-text-2)" }}><ArrowLeft size={14} /> Dashboard</Link>
      <div className="mt-4 flex items-center justify-between">
        <h1 className="font-serif-arc text-3xl font-black">Quizzes</h1>
        <ArcButton variant="ghoulax" to="/admin/quizzes/new" data-testid="new-quiz"><Plus size={16} /> New Quiz</ArcButton>
      </div>

      <div className="mt-8 space-y-3">
        {quizzes.length === 0 && <p className="text-sm" style={{ color: "var(--arc-text-2)" }}>No quizzes yet. Create your first one.</p>}
        {quizzes.map((q) => {
          const accent = q.encyclopedia === "zyfley" ? "zyfley-accent" : "ghoulax-accent";
          return (
            <div key={q.slug} data-testid={`quiz-row-${q.slug}`} className="flex flex-wrap items-center justify-between gap-3 rounded-md border p-4 shadow-sm" style={{ backgroundColor: "var(--arc-card)" }}>
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Link to={`/quiz/${q.slug}`} className={`font-serif-arc font-bold hover:underline ${accent}`}>{q.title}</Link>
                  {q.label && <LabelBadge label={q.label} />}
                  <span className={`inline-flex items-center gap-1 text-xs ${q.published ? "text-emerald-600" : ""}`} style={q.published ? {} : { color: "var(--arc-text-2)" }}>
                    {q.published ? <><Eye size={12} /> Published</> : <><EyeOff size={12} /> Draft</>}
                  </span>
                </div>
                <p className="mt-1 text-xs" style={{ color: "var(--arc-text-2)" }}>/{q.slug} · {q.encyclopedia} · {q.questions?.length || 0} questions · {q.levels?.length || 0} levels</p>
              </div>
              <div className="flex gap-2">
                <Link to={`/admin/quizzes/${q.slug}`} data-testid={`edit-quiz-${q.slug}`} className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm font-semibold"><Pencil size={14} /> Edit</Link>
                <button onClick={() => del(q.slug)} data-testid={`delete-quiz-${q.slug}`} className="inline-flex items-center gap-1 rounded border px-3 py-1.5 text-sm font-semibold text-red-600"><Trash2 size={14} /></button>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
